import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icone } from './Icone';

/**
 * Fenêtre modale (charte, doc 06). Réservée à la saisie ponctuelle —
 * création et édition d'un objet référentiel — jamais à une alerte, qui
 * reste un bandeau en flux (§ 6.5).
 *
 * Le voile est de l'encre translucide, le panneau du papier posé dessus,
 * cerné d'un filet plutôt que d'une ombre portée. Échap et clic sur le
 * voile referment ; le défilement du fond est verrouillé le temps de la
 * saisie ; le focus part sur le premier champ.
 */
export function Modale({
  titre,
  surtitre,
  ouverte,
  onFermer,
  taille = 'moyenne',
  children,
}: {
  titre: string;
  surtitre?: string;
  ouverte: boolean;
  onFermer: () => void;
  taille?: 'moyenne' | 'large';
  children: ReactNode;
}) {
  const panneau = useRef<HTMLDivElement>(null);

  // Les appelants passent une fermeture en ligne, dont l'identité change à
  // chaque frappe. On la garde dans une référence pour que l'effet ci-dessous
  // ne dépende que de `ouverte` : sans cela, chaque saisie rejouait l'effet et
  // renvoyait le focus sur le premier champ.
  const fermerRef = useRef(onFermer);
  fermerRef.current = onFermer;

  useEffect(() => {
    if (!ouverte) return;
    const surEchap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermerRef.current();
    };
    document.addEventListener('keydown', surEchap);
    const defilementInitial = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus sur le premier élément saisissable, sans voler la sélection d'un
    // bouton d'action qui aurait sa propre logique.
    panneau.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    return () => {
      document.removeEventListener('keydown', surEchap);
      document.body.style.overflow = defilementInitial;
    };
  }, [ouverte]);

  if (!ouverte) return null;

  return createPortal(
    <div
      className="modale__voile"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onFermer();
      }}
    >
      <div
        ref={panneau}
        className={`modale modale--${taille}`}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
      >
        <header className="modale__tete">
          <div>
            {surtitre && <p className="surtitre">{surtitre}</p>}
            <h2 className="modale__titre">{titre}</h2>
          </div>
          <button type="button" className="modale__fermer" onClick={onFermer} aria-label="Fermer">
            <Icone nom="fermer" taille={18} />
          </button>
        </header>
        <div className="modale__corps">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
